import { useState } from "react";
import { Button } from "@/components/ui/button";
import { useTheme } from "@/App";
import { 
  LayoutDashboard, 
  Save, 
  Sparkles, 
  ChevronRight, 
  ChevronLeft,
  X,
  CheckCircle
} from "lucide-react";

const OnboardingModal = ({ isOpen, onComplete, appId }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  const handleComplete = () => {
    if (appId) {
      localStorage.setItem(`onboarding_completed_${appId}`, "true");
    }
    onComplete?.();
  };

  const handleSkip = () => {
    if (appId) {
      localStorage.setItem(`onboarding_completed_${appId}`, "true");
    }
    onComplete?.();
  };

  const txt = isDark ? 'text-slate-300' : 'text-gray-700';
  const txtMuted = isDark ? 'text-slate-400' : 'text-gray-500';
  const txtBold = isDark ? 'text-white' : 'text-gray-900';

  const steps = [
    {
      icon: LayoutDashboard,
      iconColor: "#00677F",
      iconBg: "bg-[#00677F]/10",
      title: "Welcome to Your Application!",
      subtitle: "Let's show you around",
      content: (
        <>
          <p className={`${txt} mb-4`}>
            Your <span className="text-[#00677F] font-semibold">Dashboard</span> is your home base. 
            From there you can:
          </p>
          <ul className={`space-y-3 ${txtMuted}`}>
            {["Track your application progress", "View upcoming deadlines", "Contact your enrollment advisor", "Manage your documents"].map((item, i) => (
              <li key={i} className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-[#739600] mt-0.5 flex-shrink-0" />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </>
      )
    },
    {
      icon: Save,
      iconColor: "#739600",
      iconBg: "bg-[#739600]/10",
      title: "Your Progress is Auto-Saved",
      subtitle: "Pick up where you left off",
      content: (
        <>
          <p className={`${txt} mb-4`}>
            Don't worry about losing your work! Your application 
            <span className="text-[#739600] font-semibold"> saves automatically</span> as you fill out each section.
          </p>
          <div className={`rounded-xl p-4 mb-4 ${isDark ? 'bg-[#739600]/10 border border-[#739600]/20' : 'bg-[#739600]/5 border border-[#739600]/15'}`}>
            <p className="text-[#739600] text-sm font-medium mb-2">Pro Tips:</p>
            <ul className={`space-y-2 ${txtMuted} text-sm`}>
              {["Use the navigation on the left to jump between sections", "Green checkmarks indicate completed sections", "You can return anytime from your Dashboard"].map((tip, i) => (
                <li key={i} className="flex items-start gap-2">
                  <span className="text-[#739600]">•</span>
                  <span>{tip}</span>
                </li>
              ))}
            </ul>
          </div>
          <p className={`${txtMuted} text-sm`}>
            Click <span className={`${txtBold} font-medium`}>"Dashboard"</span> in the top left to return to your Dashboard at any time.
          </p>
        </>
      )
    },
    {
      icon: Sparkles,
      iconColor: "#7B68EE",
      iconBg: "bg-[#7B68EE]/10",
      title: "Meet Journey, Your AI Assistant",
      subtitle: "Get help anytime you need it",
      content: (
        <>
          <p className={`${txt} mb-4`}>
            <span className="text-[#7B68EE] font-semibold">Journey</span> is your personal AI assistant, 
            ready to help you throughout your application process.
          </p>
          <div className={`rounded-xl p-4 mb-4 ${isDark ? 'bg-gradient-to-r from-[#7B68EE]/10 to-[#00677F]/10 border border-[#7B68EE]/20' : 'bg-gradient-to-r from-[#7B68EE]/5 to-[#00677F]/5 border border-[#7B68EE]/15'}`}>
            <p className={`${txtBold} text-sm font-medium mb-3`}>Ask Journey about:</p>
            <div className={`grid grid-cols-2 gap-2 text-sm ${txt}`}>
              {["Program requirements", "Application tips", "Document help", "Deadlines & dates"].map((topic, i) => (
                <div key={i} className="flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-[#7B68EE]" />
                  <span>{topic}</span>
                </div>
              ))}
            </div>
          </div>
          <p className={`${txtMuted} text-sm`}>
            Look for the <span className="text-[#7B68EE] font-medium">"Ask Journey"</span> button 
            in the top right corner of your screen.
          </p>
        </>
      )
    }
  ];

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setIsAnimating(true);
      setTimeout(() => {
        setCurrentStep(currentStep + 1);
        setIsAnimating(false);
      }, 200);
    } else {
      handleComplete();
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setIsAnimating(true);
      setTimeout(() => {
        setCurrentStep(currentStep - 1);
        setIsAnimating(false);
      }, 200);
    }
  };

  if (!isOpen) return null;

  const step = steps[currentStep];
  const Icon = step.icon;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className={`absolute inset-0 ${isDark ? 'bg-black/80' : 'bg-black/40'} backdrop-blur-sm`} />
      
      <div className={`relative rounded-2xl max-w-lg w-full mx-4 overflow-hidden shadow-2xl ${isDark ? 'bg-[#11161F] border border-white/10' : 'bg-white border border-gray-200'}`}>
        <div className={`absolute top-0 left-0 right-0 h-1 ${isDark ? 'bg-white/5' : 'bg-gray-200'}`}>
          <div 
            className="h-full bg-gradient-to-r from-[#00677F] to-[#7B68EE] transition-all duration-500"
            style={{ width: `${((currentStep + 1) / steps.length) * 100}%` }}
          />
        </div>

        <button
          onClick={handleSkip}
          className={`absolute top-4 right-4 transition-colors text-sm flex items-center gap-1 ${isDark ? 'text-slate-500 hover:text-white' : 'text-gray-400 hover:text-gray-700'}`}
        >
          Skip tour
          <X className="w-4 h-4" />
        </button>

        <div className={`p-8 pt-12 transition-opacity duration-200 ${isAnimating ? 'opacity-0' : 'opacity-100'}`}>
          <div className={`w-16 h-16 rounded-2xl ${step.iconBg} flex items-center justify-center mx-auto mb-6`}>
            <Icon className="w-8 h-8" style={{ color: step.iconColor }} />
          </div>

          <div className="text-center mb-6">
            <h2 className={`text-2xl font-bold mb-2 ${txtBold}`}>{step.title}</h2>
            <p className={txtMuted}>{step.subtitle}</p>
          </div>

          <div className="mb-8">
            {step.content}
          </div>

          <div className="flex justify-center gap-2 mb-6">
            {steps.map((_, index) => (
              <div
                key={index}
                className={`h-2 rounded-full transition-all duration-300 ${
                  index === currentStep 
                    ? 'w-8 bg-[#00677F]' 
                    : index < currentStep 
                      ? 'w-2 bg-[#739600]' 
                      : isDark ? 'w-2 bg-white/20' : 'w-2 bg-gray-200'
                }`}
              />
            ))}
          </div>

          <div className="flex gap-3">
            {currentStep > 0 && (
              <Button
                onClick={handleBack}
                variant="outline"
                className={`flex-1 h-12 bg-transparent rounded-xl ${isDark ? 'border-white/10 hover:border-white/20 text-slate-300 hover:text-white' : 'border-gray-200 hover:border-gray-300 text-gray-600 hover:text-gray-900'}`}
              >
                <ChevronLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
            )}
            <Button
              onClick={handleNext}
              className={`flex-1 h-12 rounded-xl font-medium transition-all ${
                currentStep === steps.length - 1
                  ? 'bg-gradient-to-r from-[#00677F] to-[#7B68EE] hover:opacity-90 text-white shadow-[0_0_20px_rgba(0,180,216,0.3)]'
                  : 'bg-[#00677F] hover:bg-[#135163] text-white'
              }`}
            >
              {currentStep === steps.length - 1 ? (
                <>Get Started <CheckCircle className="w-4 h-4 ml-2" /></>
              ) : (
                <>Next <ChevronRight className="w-4 h-4 ml-2" /></>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OnboardingModal;
